import axios from 'axios';
import { AppName } from 'sl-api-connector/types';
import { GetContentGenerationResponse } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Generate/types';
import { getAppName, getQueryParamValue } from 'src/utils/app';
import {
  GenerateContentForSkuRequest,
  GenerateContentForSkuRequestSchema,
  GenerateContentForSkuResponse,
  GetContentGenerationStatusRequest,
  GetContentGenerationStatusRequestSchema,
  GetContentGenerationStatusResponse,
  GetCurrentContentRequest,
  GetCurrentContentRequestSchema,
  GetCurrentContentResponse,
  GetProductKeywordRequest,
  GetProductKeywordRequestSchema,
  GetProductKeywordResponse,
  ContentLibraryManagementRequest,
  ContentLibraryManagementRequestSchema,
  ContentLibraryManagementResponse,
  GetProfileRequest,
  GetProfileResponse,
  GenerateContentForSkusRequestSchema,
  GenerateContentForSkusRequest,
  GenerateContentForSkusResponse,
  CreateProjectRequest,
  CreateProjectRequestSchema,
  UpdateProjectRequest,
  UpdateProjectRequestSchema,
  UpdateProjectResponse,
  ExportContentRequest,
  ExportContentRequestSchema,
  ExportContentResponse,
  ProductKeywordsComparisonRequest,
  ProductKeywordsComparisonRequestSchema,
  ComparisonResponse,
  GetContentGenerationRequestsByStatusRequest,
  GetContentGenerationRequestsByStatusRequestSchema,
  GetContentGenerationRequestsByProjectRequest,
  GetContentGenerationRequestsByProjectRequestSchema,
  GetContentLibraryRequest,
  GetContentLibraryRequestSchema,
  GetContentLibraryResponse,
  StreamCompareProductKeywordsSummaryRequest,
  StreamCompareProductKeywordsSummaryRequestSchema,
  GetProjectsRequest,
  GetProjectsRequestSchema,
  GetProjectsResponse,
  CreditStatusResponse,
  GetContentGenerationMetricsRequest,
  GetContentGenerationMetricsRequestSchema,
  GetContentGenerationMetricsResponse,
  EditGeneratedContentRequest,
  EditGeneratedContentRequestSchema,
  EditGeneratedContentResponse
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/apiTypes';

/**
 * Type guard to check if an error is an Axios error with a response
 */
const isAxiosErrorWithResponse = (error: unknown): error is { response: { status: number } } => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as any).response === 'object' &&
    (error as any).response !== null &&
    'status' in (error as any).response
  );
};

/**
 * Gets the base URL for API requests, using the same logic as axios configuration
 * but ensuring BeaconContent calls go to the correct domain
 */
const getApiBaseUrl = (): string => {
  // Use axios configured baseURL if available
  if (axios.defaults.baseURL) {
    return axios.defaults.baseURL;
  }

  // Fallback: construct the base URL using current hostname
  const { hostname } = window.location;
  let apiEndpoint = '';

  // Keep localhost and ad domains as-is (same logic as buildBaseTarget)
  if (hostname.includes('localhost') || hostname.startsWith('ad')) {
    apiEndpoint = hostname;
  } else if (hostname.indexOf('stackline.com') >= 0) {
    // For BeaconContent APIs, we want to use the beacon domain, not api domain
    // This ensures beacon-localhost.stackline.com stays as beacon-localhost.stackline.com
    apiEndpoint = hostname;
  } else {
    apiEndpoint = 'beacon.stackline.com'; // Default to beacon domain for BeaconContent
  }

  return `https://${apiEndpoint}`;
};

const getRetailerIdFromQueryParam = (): number | undefined => {
  const rid = getQueryParamValue('rid', '');
  const retailerId = Number(rid);
  if (!Number.isFinite(retailerId) || retailerId <= 0) {
    return undefined;
  }
  return retailerId;
};

const resolveRetailerId = (bodyRetailerId?: number): number => {
  const queryRetailerId = getRetailerIdFromQueryParam();
  if (queryRetailerId) {
    return queryRetailerId;
  }
  if (typeof bodyRetailerId === 'number' && Number.isFinite(bodyRetailerId) && bodyRetailerId > 0) {
    return bodyRetailerId;
  }
  throw new Error('Retailer ID is required (missing rid query param)');
};

type GetProjectsRequestInput = Omit<GetProjectsRequest, 'retailerId'> & Partial<Pick<GetProjectsRequest, 'retailerId'>>;
type GetContentGenerationStatusRequestInput = Omit<GetContentGenerationStatusRequest, 'retailerId'> &
  Partial<Pick<GetContentGenerationStatusRequest, 'retailerId'>>;
type GetContentGenerationRequestsByProjectRequestInput = Omit<GetContentGenerationRequestsByProjectRequest, 'retailerId'> &
  Partial<Pick<GetContentGenerationRequestsByProjectRequest, 'retailerId'>>;
type GenerateContentForSkuRequestInput = Omit<GenerateContentForSkuRequest, 'retailerId'> &
  Partial<Pick<GenerateContentForSkuRequest, 'retailerId'>>;
type GenerateContentForSkusRequestInput = Omit<GenerateContentForSkusRequest, 'retailerId'> &
  Partial<Pick<GenerateContentForSkusRequest, 'retailerId'>>;
type CreateProjectRequestInput = Omit<CreateProjectRequest, 'retailerId'> & Partial<Pick<CreateProjectRequest, 'retailerId'>>;
type UpdateProjectRequestInput = Omit<UpdateProjectRequest, 'retailerId'> & Partial<Pick<UpdateProjectRequest, 'retailerId'>>;
type GetContentLibraryRequestInput = Omit<GetContentLibraryRequest, 'retailerId'> &
  Partial<Pick<GetContentLibraryRequest, 'retailerId'>>;

export const apiGenerateContentForSkus = async (body: GenerateContentForSkusRequestInput) => {
  try {
    const payload: GenerateContentForSkusRequest = { ...body, retailerId: resolveRetailerId(body.retailerId) };
    const validationResult = GenerateContentForSkusRequestSchema.safeParse(payload);

    if (!validationResult.success) {
      console.error('GenerateContentForSkus validation failed:', validationResult);
      throw new Error('Invalid request data for GenerateContentForSkus');
    }

    const res = await axios.post<GenerateContentForSkusResponse>('/api/beaconcontent/GenerateContent', validationResult.data);

    if (!res.data.isSuccess) {
      throw new Error('Failed to generate content for SKUs');
    }

    return res;
  } catch (error) {
    if (isAxiosErrorWithResponse(error) && error.response.status === 401) {
      throw new Error('Unauthorized: Please log in again');
    }
    throw error;
  }
};

export const apiGenerateContentForSku = async (body: GenerateContentForSkuRequestInput) => {
  try {
    const payload: GenerateContentForSkuRequest = { ...body, retailerId: resolveRetailerId(body.retailerId) };
    const validationResult = GenerateContentForSkuRequestSchema.safeParse(payload);

    if (!validationResult.success) {
      console.error('GenerateContentForSku validation failed:', validationResult);
      throw new Error('Invalid request data for GenerateContentForSku');
    }

    const res = await axios.post<GenerateContentForSkuResponse>(
      '/api/beaconcontent/GenerateContentForSku',
      validationResult.data
    );

    if (!res.data.isSuccess) {
      throw new Error('Failed to generate content for SKU: ' + validationResult.data.retailerSku);
    }

    return res;
  } catch (error) {
    if (isAxiosErrorWithResponse(error) && error.response.status === 401) {
      throw new Error('Unauthorized: Please log in again');
    }
    throw error;
  }
};

export const apiGetContentGenerationStatus = async (body: GetContentGenerationStatusRequestInput) => {
  try {
    const payload: GetContentGenerationStatusRequest = { ...body, retailerId: resolveRetailerId(body.retailerId) };
    const validationResult = GetContentGenerationStatusRequestSchema.safeParse(payload);

    if (!validationResult.success) {
      console.error('GetContentGenerationStatus validation failed:', validationResult);
      throw new Error('Invalid request data for GetContentGenerationStatus');
    }

    const res = await axios.post<GetContentGenerationStatusResponse>(
      '/api/beaconcontent/GetContentGenerationStatus',
      validationResult.data
    );
    if (!res.data.data.success) {
      throw new Error('Failed to get content generation status');
    }

    return res.data.data;
  } catch (error) {
    if (isAxiosErrorWithResponse(error) && error.response.status === 401) {
      throw new Error('Unauthorized: Please log in again');
    }
    throw error;
  }
};

export const apiGetCurrentContent = async (body: GetCurrentContentRequest) => {
  try {
    const validationResult = GetCurrentContentRequestSchema.safeParse(body);

    if (!validationResult.success) {
      console.error('GetCurrentContent validation failed:', validationResult);
      throw new Error('Invalid request data for GetCurrentContent');
    }

    const res = await axios.post<GetCurrentContentResponse>('/api/beaconcontent/GetCurrentContent', body);

    return res;
  } catch (error) {
    if (isAxiosErrorWithResponse(error) && error.response.status === 401) {
      throw new Error('Unauthorized: Please log in again');
    }
    throw error;
  }
};

export const apiGetProductKeywords = async (body: GetProductKeywordRequest) => {
  try {
    const validationResult = GetProductKeywordRequestSchema.safeParse(body);

    if (!validationResult.success) {
      console.error('GetProductKeywords validation failed:', validationResult);
      throw new Error('Invalid request data for GetProductKeywords');
    }

    const res = await axios.post<GetProductKeywordResponse>('/api/beaconcontent/GetProductKeywords', body);
    if (!res.data.data.isSuccess) {
      throw new Error('Failed to get product keywords');
    }
    return res;
  } catch (error) {
    if (isAxiosErrorWithResponse(error) && error.response.status === 401) {
      throw new Error('Unauthorized: Please log in again');
    }
    throw error;
  }
};

/**
 * Saves content generation as a draft
 */
export const apiSaveAsDraft = async (body: ContentLibraryManagementRequest) => {
  try {
    const validationResult = ContentLibraryManagementRequestSchema.safeParse(body);

    if (!validationResult.success) {
      console.error('SaveAsDraft validation failed:', validationResult);
      throw new Error('Invalid request data for SaveAsDraft');
    }

    const res = await axios.post<ContentLibraryManagementResponse>('/api/beaconcontent/SaveAsDraft', body);

    if (res.status !== 200) {
      throw new Error('Failed to save content as draft');
    }

    return res;
  } catch (error) {
    if (isAxiosErrorWithResponse(error) && error.response.status === 401) {
      throw new Error('Unauthorized: Please log in again');
    }
    throw error;
  }
};

/**
 * Fetches a profile by ID and optionally a specific version
 */
export const apiGetProfile = async (params: GetProfileRequest) => {
  try {
    const res = await axios.get<GetProfileResponse>('/api/beaconcontent/GetProfile', {
      params: {
        profileId: params.profileId,
        version: params.version
      }
    });

    if (!res.data.isSuccess) {
      throw new Error('Failed to fetch profile for the given ID: ' + params.profileId);
    }

    return res.data;
  } catch (error) {
    if (isAxiosErrorWithResponse(error) && error.response.status === 401) {
      throw new Error('Unauthorized: Please log in again');
    }
    throw error;
  }
};

export const apiSaveAsMaster = async (body: ContentLibraryManagementRequest) => {
  try {
    const validationResult = ContentLibraryManagementRequestSchema.safeParse(body);

    if (!validationResult.success) {
      console.error('SaveAsMaster validation failed:', validationResult);
      throw new Error('Invalid request data for SaveAsMaster');
    }

    const res = await axios.post<ContentLibraryManagementResponse>('/api/beaconcontent/SaveAsMaster', body);

    if (res.status !== 200) {
      throw new Error('Failed to save content as master');
    }

    return res;
  } catch (error) {
    if (isAxiosErrorWithResponse(error) && error.response.status === 401) {
      throw new Error('Unauthorized: Please log in again');
    }
    throw error;
  }
};

/**
 * Archives content generation
 */
export const apiArchiveContent = async (body: ContentLibraryManagementRequest) => {
  try {
    const validationResult = ContentLibraryManagementRequestSchema.safeParse(body);

    if (!validationResult.success) {
      console.error('ArchiveContent validation failed:', validationResult);
      throw new Error('Invalid request data for ArchiveContent');
    }

    const res = await axios.post<ContentLibraryManagementResponse>('/api/beaconcontent/ArchiveContent', body);

    if (res.status !== 200) {
      throw new Error('Failed to archive content');
    }

    return res;
  } catch (error) {
    if (isAxiosErrorWithResponse(error) && error.response.status === 401) {
      throw new Error('Unauthorized: Please log in again');
    }
    throw error;
  }
};

/**
 * Export content for comparison between current and generated content
 */
export const apiExportContent = async (body: ExportContentRequest) => {
  try {
    const validationResult = ExportContentRequestSchema.safeParse(body);

    if (!validationResult.success) {
      console.error('ExportContent validation failed:', validationResult);
      throw new Error('Invalid request data for ExportContent');
    }

    const res = await axios.post<ExportContentResponse>('/api/beaconcontent/ExportContent', body);

    if (!res.data.isSuccess) {
      throw new Error('Failed to export content');
    }

    return res;
  } catch (error) {
    if (isAxiosErrorWithResponse(error) && error.response.status === 401) {
      throw new Error('Unauthorized: Please log in again');
    }
    throw error;
  }
};

/**
 * Compare keyword analysis between current and new product content versions
 */
export const apiCompareProductKeywords = async (body: ProductKeywordsComparisonRequest) => {
  try {
    const validationResult = ProductKeywordsComparisonRequestSchema.safeParse(body);

    if (!validationResult.success) {
      console.error('CompareProductKeywords validation failed:', validationResult);
      throw new Error('Invalid request data for CompareProductKeywords');
    }

    const res = await axios.post<ComparisonResponse>('/api/beaconcontent/CompareProductKeywords', body);

    if (!res.data.isSuccess) {
      throw new Error(res.data.message || 'Failed to compare product keywords');
    }

    return res;
  } catch (error) {
    if (isAxiosErrorWithResponse(error) && error.response.status === 401) {
      throw new Error('Unauthorized: Please log in again');
    }
    throw error;
  }
};

/**
 * Stream summary for Compare Product Keywords
 * Returns a readable stream of text chunks for real-time summary generation
 */
export const apiStreamCompareProductKeywordsSummary = async (
  body: StreamCompareProductKeywordsSummaryRequest,
  abortSignal?: AbortSignal
) => {
  try {
    const validationResult = StreamCompareProductKeywordsSummaryRequestSchema.safeParse(body);

    if (!validationResult.success) {
      console.error('StreamCompareProductKeywordsSummary validation failed:', validationResult.error);
      console.error('Validation errors:', validationResult.error.errors);
      throw new Error('Invalid request data for StreamCompareProductKeywordsSummary');
    }

    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/beaconcontent/stream_compare_product_keywords_summary`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        'Cache-Control': 'no-cache'
      },
      body: JSON.stringify(body),
      credentials: 'include', // Includes cookies for cross-origin requests like axios withCredentials: true
      signal: abortSignal
    });

    if (response.status === 401) {
      throw new Error('Unauthorized: Please log in again');
    }

    if (!response.ok) {
      throw new Error(`Failed to stream summary: ${response.statusText}`);
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    return response.body;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Unauthorized')) {
      throw error;
    }
    throw error;
  }
};

/**
 * Get content generation requests by status with optional retailer SKUs filter
 */
export const apiGetContentGenerationRequestsByStatus = async (body: GetContentGenerationRequestsByStatusRequest) => {
  try {
    const validationResult = GetContentGenerationRequestsByStatusRequestSchema.safeParse(body);

    if (!validationResult.success) {
      console.error('GetContentGenerationRequestsByStatus validation failed:', validationResult);
      throw new Error('Invalid request data for GetContentGenerationRequestsByStatus');
    }

    const res = await axios.post<GetContentGenerationResponse>(
      '/api/beaconcontent/GetContentGenerationRequestsByStatus',
      body
    );

    return res;
  } catch (error) {
    if (isAxiosErrorWithResponse(error) && error.response.status === 401) {
      throw new Error('Unauthorized: Please log in again');
    }
    throw error;
  }
};

/**
 * Get content generation requests by project with optional status and retailer SKUs filter
 */
export const apiGetContentGenerationRequestsByProject = async (body: GetContentGenerationRequestsByProjectRequestInput) => {
  // Only allow this API to be called in Beacon app
  if (getAppName() !== AppName.Beacon) {
    return null;
  }

  try {
    const payload: GetContentGenerationRequestsByProjectRequest = { ...body, retailerId: resolveRetailerId(body.retailerId) };
    const validationResult = GetContentGenerationRequestsByProjectRequestSchema.safeParse(payload);

    if (!validationResult.success) {
      console.error('GetContentGenerationRequestsByProject validation failed:', validationResult);
      throw new Error('Invalid request data for GetContentGenerationRequestsByProject');
    }

    const res = await axios.post<GetContentGenerationResponse>(
      '/api/beaconcontent/GetContentGenerationRequestsByProject',
      validationResult.data
    );

    return res;
  } catch (error) {
    if (isAxiosErrorWithResponse(error) && error.response.status === 401) {
      throw new Error('Unauthorized: Please log in again');
    }
    throw error;
  }
};

/**
 * Get content library with unified view of content generations and master content
 */
export const apiGetContentLibrary = async (body: GetContentLibraryRequestInput) => {
  try {
    const payload: GetContentLibraryRequest = { ...body, retailerId: resolveRetailerId(body.retailerId) };
    const validationResult = GetContentLibraryRequestSchema.safeParse(payload);

    if (!validationResult.success) {
      console.error('GetContentLibrary validation failed:', validationResult);
      throw new Error('Invalid request data for GetContentLibrary');
    }

    const res = await axios.post<GetContentLibraryResponse>(
      '/api/beaconcontent/GetContentLibrary',
      validationResult.data
    );

    return res;
  } catch (error) {
    if (isAxiosErrorWithResponse(error) && error.response.status === 401) {
      throw new Error('Unauthorized: Please log in again');
    }
    throw error;
  }
};

/**
 * Get the current content-generation credit status for the signed-in division
 * No parameters needed - the API resolves beaconClientId from user context
 */
export const apiGetCreditStatus = async (): Promise<CreditStatusResponse> => {
  try {
    const res = await axios.get('/api/beaconcontent/getCreditStatus');
    return res.data;
  } catch (error) {
    if (isAxiosErrorWithResponse(error) && error.response.status === 401) {
      throw new Error('Unauthorized: Please log in again');
    }
    throw error;
  }
};

/**
 * Get all projects for the authenticated beacon client with optional filtering and pagination
 */
export const apiGetProjects = async (body: GetProjectsRequestInput = {}) => {
  try {
    const payload: GetProjectsRequest = { ...body, retailerId: resolveRetailerId(body.retailerId) };
    const validationResult = GetProjectsRequestSchema.safeParse(payload);

    if (!validationResult.success) {
      console.error('GetProjects validation failed:', validationResult);
      throw new Error('Invalid request data for GetProjects');
    }

    const res = await axios.post<GetProjectsResponse>('/api/beaconcontent/GetProjects', validationResult.data);

    if (!res.data) {
      throw new Error('Failed to get projects');
    }

    return res;
  } catch (error) {
    if (isAxiosErrorWithResponse(error) && error.response.status === 401) {
      throw new Error('Unauthorized: Please log in again');
    }
    throw error;
  }
};

export const createProject = async (body: CreateProjectRequestInput) => {
  try {
    const payload: CreateProjectRequest = { ...body, retailerId: resolveRetailerId(body.retailerId) };
    const validationResult = CreateProjectRequestSchema.safeParse(payload);
    if (!validationResult.success) {
      console.error('CreateProject validation failed:', validationResult);
      throw new Error('Invalid request data for CreateProject');
    }

    return (await axios.post('/api/beaconcontent/CreateProject', validationResult.data)).data;
  } catch (error) {
    if (isAxiosErrorWithResponse(error) && error.response.status === 401) {
      throw new Error('Unauthorized: Please log in again');
    }
    throw error;
  }
};

/**
 * Update an existing project
 */
export const apiUpdateProject = async (body: UpdateProjectRequestInput) => {
  try {
    const payload: UpdateProjectRequest = { ...body, retailerId: resolveRetailerId(body.retailerId) };
    const validationResult = UpdateProjectRequestSchema.safeParse(payload);
    if (!validationResult.success) {
      console.error('UpdateProject validation failed:', validationResult);
      throw new Error('Invalid request data for UpdateProject');
    }

    const res = await axios.post<UpdateProjectResponse>('/api/beaconcontent/UpdateProject', validationResult.data);

    if (!res.data.success) {
      throw new Error(res.data.error || 'Failed to update project');
    }

    return res;
  } catch (error) {
    if (isAxiosErrorWithResponse(error) && error.response.status === 401) {
      throw new Error('Unauthorized: Please log in again');
    }
    throw error;
  }
};

/**
 * Get content generation metrics for candidate content
 * Scores content using different metric types (utilization, keyword usage, popularity, productivity)
 */
export const apiGetContentGenerationMetrics = async (body: GetContentGenerationMetricsRequest) => {
  try {
    const validationResult = GetContentGenerationMetricsRequestSchema.safeParse(body);

    if (!validationResult.success) {
      console.error('GetContentGenerationMetrics validation failed:', validationResult);
      throw new Error('Invalid request data for GetContentGenerationMetrics');
    }

    const res = await axios.post<GetContentGenerationMetricsResponse>(
      '/api/beaconcontent/GetContentGenerationMetrics',
      body
    );

    return res;
  } catch (error) {
    if (isAxiosErrorWithResponse(error) && error.response.status === 401) {
      throw new Error('Unauthorized: Please log in again');
    }
    throw error;
  }
};

/**
 * Edit generated content for a SKU
 * Updates the title, bullets, and description for a generated content item
 */
export const apiEditGeneratedContent = async (body: EditGeneratedContentRequest) => {
  try {
    const validationResult = EditGeneratedContentRequestSchema.safeParse(body);

    if (!validationResult.success) {
      console.error('EditGeneratedContent validation failed:', validationResult);
      throw new Error('Invalid request data for EditGeneratedContent');
    }

    const res = await axios.post<EditGeneratedContentResponse>(
      '/api/beaconcontent/EditGeneratedContent',
      body
    );

    if (!res.data.isSuccess) {
      throw new Error(res.data.message || 'Failed to edit generated content');
    }

    return res;
  } catch (error) {
    if (isAxiosErrorWithResponse(error) && error.response.status === 401) {
      throw new Error('Unauthorized: Please log in again');
    }
    throw error;
  }
};

